import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';
import { format, differenceInDays } from 'date-fns';
import { toast } from 'sonner';
import { 
  Plus, Search, Filter, AlertTriangle, CheckCircle, Clock,
  ChevronRight, ChevronDown, RefreshCw, Download, X, Factory, FileText,
  User, Calendar, Image, Upload, PenTool, Hash, Building2,
  AlertCircle, ClipboardList, FileCheck, ShieldCheck, Timer, MoreHorizontal,
  ExternalLink, Edit2, Save
} from 'lucide-react';

const BMS_DEPARTMENTS = {
  "0100": { name: "Sales and Estimating", color: "bg-blue-100 text-blue-800" },
  "0200": { name: "Project Administration", color: "bg-purple-100 text-purple-800" },
  "0300": { name: "Supply Chain", color: "bg-green-100 text-green-800" },
  "0400": { name: "Engineering", color: "bg-yellow-100 text-yellow-800" },
  "0500": { name: "Manufacturing", color: "bg-orange-100 text-orange-800" },
  "0600": { name: "Quality Control", color: "bg-red-100 text-red-700" },
  "0700": { name: "Aftermarket", color: "bg-teal-100 text-teal-800" },
  "0800": { name: "Accounts", color: "bg-indigo-100 text-indigo-800" },
  "0900": { name: "BMS Administration", color: "bg-pink-100 text-pink-800" },
  "1000": { name: "Human Resources", color: "bg-cyan-100 text-cyan-800" }
};

const NC_TYPES = [
  { value: 'product', label: 'Product' },
  { value: 'process', label: 'Process' },
  { value: 'supplier', label: 'Supplier' },
  { value: 'customer', label: 'Customer' },
  { value: 'internal', label: 'Internal' }
];

const SEVERITY_OPTIONS = [
  { value: 'critical', label: 'Critical', color: 'bg-red-100 border-red-300', textColor: '#E00702' },
  { value: 'major', label: 'Major', color: 'bg-orange-100 border-orange-300', textColor: '#FB9703' },
  { value: 'minor', label: 'Minor', color: 'bg-yellow-100 border-yellow-300', textColor: '#DFC03D' }
];

const AREA_OPTIONS = [
  { value: 'incoming', label: 'Incoming Inspection' },
  { value: 'in_process', label: 'In-Process' },
  { value: 'final_inspection', label: 'Final Inspection' },
  { value: 'field', label: 'Field/Customer' },
  { value: 'audit', label: 'Audit Finding' }
];

const DISPOSITION_OPTIONS = [
  { value: 'pending', label: 'Pending Review' },
  { value: 'rework', label: 'Rework' },
  { value: 'scrap', label: 'Scrap' },
  { value: 'use_as_is', label: 'Use As-Is' },
  { value: 'return_to_supplier', label: 'Return to Supplier' },
  { value: 'quarantine', label: 'Quarantine' }
];

const ONCRManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [oncrs, setOncrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOncr, setSelectedOncr] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [bulkUploadResult, setBulkUploadResult] = useState(null);
  const [filters, setFilters] = useState({ department_code: '', status: '', severity: '', job_number: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRef = useRef(null);
  
  // Check for highlight parameter (coming from CAPA)
  const highlightId = searchParams.get('highlight');

  const fetchOncrs = useCallback(async () => {
    try {
      let url = '/oncrs';
      const params = new URLSearchParams();
      if (filters.department_code) params.append('department_code', filters.department_code);
      if (filters.status) params.append('status', filters.status);
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.job_number) params.append('job_number', filters.job_number);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await apiClient.get(url);
      setOncrs(response.data);
      
      // Auto-select ONCR if highlight parameter is present
      if (highlightId && response.data) {
        const targetOncr = response.data.find(o => o.id === highlightId);
        if (targetOncr) {
          setSelectedOncr(targetOncr);
        }
      }
    } catch (error) {
      console.error('Error fetching ONCRs:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, highlightId]);

  useEffect(() => {
    fetchOncrs();
  }, [fetchOncrs]);

  const filteredOncrs = oncrs.filter(oncr => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      oncr.oncr_number?.toLowerCase().includes(query) ||
      oncr.job_number?.toLowerCase().includes(query) ||
      oncr.description?.toLowerCase().includes(query) ||
      oncr.department?.toLowerCase().includes(query)
    );
  });

  const getDaysOpen = (createdAt) => {
    return differenceInDays(new Date(), new Date(createdAt));
  };

  const getAgingColor = (days) => {
    if (days > 30) return 'text-red-600 bg-red-50';
    if (days > 14) return 'text-orange-600 bg-orange-50';
    if (days > 7) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  // Bulk Upload Handlers
  const handleDownloadTemplate = async () => {
    try {
      const response = await apiClient.get('/oncrs/template/download', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'ONCR_Bulk_Upload_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkUploadFile) {
      toast.error('Please select a file to upload');
      return;
    }

    setBulkUploadLoading(true);
    setBulkUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', bulkUploadFile);

      const response = await apiClient.post('/oncrs/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setBulkUploadResult(response.data);
      
      if (response.data.summary.inserted > 0) {
        toast.success(`Successfully uploaded ${response.data.summary.inserted} ONCRs`);
        fetchOncrs(); // Refresh the list
      }
      
      if (response.data.summary.failed > 0) {
        toast.warning(`${response.data.summary.failed} records had errors`);
      }
    } catch (error) {
      console.error('Bulk upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process bulk upload');
      setBulkUploadResult({
        success: false,
        message: error.response?.data?.detail || 'Upload failed',
        summary: { total_processed: 0, inserted: 0, failed: 0 },
        errors: [{ row: 'N/A', job_number: 'N/A', errors: [error.response?.data?.detail || 'Unknown error'] }]
      });
    } finally {
      setBulkUploadLoading(false);
    }
  };

  const closeBulkUploadModal = () => {
    setShowBulkUploadModal(false);
    setBulkUploadFile(null);
    setBulkUploadResult(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="oncr-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800">ONCR Management</h1>
          <p className="text-sm sm:text-base text-slate-500">Operational Non-Conformance Reports</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowBulkUploadModal(true)}
            data-testid="bulk-upload-btn"
            className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-slate-100 text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-200 transition-colors text-sm sm:text-base"
          >
            <Upload className="h-4 w-4" />
            Bulk Upload
          </button>
          <button
            onClick={() => setShowNewModal(true)}
            data-testid="new-oncr-btn"
            className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
          >
            <Plus className="h-4 w-4" />
            New ONCR
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4 shadow-sm space-y-3 sm:space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by ONCR #, Job #, description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid="oncr-search"
          />
        </div>

        {/* Filters Row */}
        <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
          <Filter className="h-4 w-4 text-slate-400 hidden sm:block" />
          
          {/* Job Number Filter */}
          <input
            type="text"
            placeholder="Job #"
            value={filters.job_number}
            onChange={(e) => setFilters({ ...filters, job_number: e.target.value })}
            className="px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-300 rounded-lg text-xs sm:text-sm w-24 sm:w-36"
            data-testid="filter-job-number"
          />
          
          <select
            value={filters.department_code}
            onChange={(e) => setFilters({ ...filters, department_code: e.target.value })}
            className="px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-300 rounded-lg text-xs sm:text-sm flex-1 sm:flex-none"
            data-testid="filter-department"
          >
            <option value="">All Depts</option>
            {Object.entries(BMS_DEPARTMENTS).map(([code, info]) => (
              <option key={code} value={code}>{code}</option>
            ))}
          </select>
          
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-300 rounded-lg text-xs sm:text-sm flex-1 sm:flex-none"
            data-testid="filter-status"
          >
            <option value="">All Status</option>
            <option value="open">Open</option>
            <option value="containment">Containment</option>
            <option value="disposition">Disposition</option>
            <option value="verification">Verification</option>
            <option value="capa_required">CAPA Req</option>
            <option value="pending_approval">Pending</option>
            <option value="closed">Closed</option>
          </select>
          
          <select
            value={filters.severity}
            onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
            className="px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-300 rounded-lg text-xs sm:text-sm flex-1 sm:flex-none"
            data-testid="filter-severity"
          >
            <option value="">Severity</option>
            <option value="critical">Critical</option>
            <option value="major">Major</option>
            <option value="minor">Minor</option>
          </select>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-4">
        <div className="bg-white rounded-xl p-2 sm:p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-1 sm:gap-2 text-slate-500 text-[10px] sm:text-sm">
            <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Open</span>
          </div>
          <p className="text-lg sm:text-2xl font-bold text-slate-800 mt-0.5 sm:mt-1">
            {oncrs.filter(o => o.status === 'open').length}
          </p>
        </div>
        <div className="bg-white rounded-xl p-2 sm:p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-1 sm:gap-2 text-yellow-500 text-[10px] sm:text-sm">
            <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">In Progress</span>
          </div>
          <p className="text-lg sm:text-2xl font-bold text-yellow-600 mt-0.5 sm:mt-1">
            {oncrs.filter(o => ['containment', 'disposition', 'verification'].includes(o.status)).length}
          </p>
        </div>
        <div className="bg-white rounded-xl p-2 sm:p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-1 sm:gap-2 text-purple-500 text-[10px] sm:text-sm">
            <FileCheck className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">CAPA Req</span>
          </div>
          <p className="text-lg sm:text-2xl font-bold text-purple-600 mt-0.5 sm:mt-1">
            {oncrs.filter(o => o.status === 'capa_required').length}
          </p>
        </div>
        <div className="hidden sm:block bg-white rounded-xl p-2 sm:p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-1 sm:gap-2 text-red-500 text-[10px] sm:text-sm">
            <AlertCircle className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Overdue (&gt;30 days)</span>
          </div>
          <p className="text-lg sm:text-2xl font-bold text-red-600 mt-0.5 sm:mt-1">
            {oncrs.filter(o => o.status !== 'closed' && getDaysOpen(o.created_at) > 30).length}
          </p>
        </div>
        <div className="hidden sm:block bg-white rounded-xl p-2 sm:p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-1 sm:gap-2 text-green-500 text-[10px] sm:text-sm">
            <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Closed (30 days)</span>
          </div>
          <p className="text-lg sm:text-2xl font-bold text-green-600 mt-0.5 sm:mt-1">
            {oncrs.filter(o => o.status === 'closed').length}
          </p>
        </div>
      </div>

      {/* ONCR List or Detail View */}
      {!selectedOncr ? (
        <div className="space-y-3 sm:space-y-4">
          {filteredOncrs.map((oncr) => (
            <div
              key={oncr.id}
              onClick={() => setSelectedOncr(oncr)}
              className="bg-white border border-slate-200 rounded-xl p-3 sm:p-6 hover:border-blue-300 cursor-pointer transition-all shadow-sm"
              data-testid={`oncr-item-${oncr.id}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  {/* Header with ONCR # and Job # */}
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-base sm:text-lg font-bold text-slate-800">{oncr.oncr_number}</span>
                    {oncr.job_number && (
                      <span className="flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-indigo-100 text-indigo-700 rounded text-xs sm:text-sm font-medium">
                        <Hash className="h-3 w-3" />
                        <span className="hidden xs:inline">Job:</span> {oncr.job_number}
                      </span>
                    )}
                    <span 
                      className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-medium ${
                        SEVERITY_OPTIONS.find(s => s.value === oncr.severity)?.color || 'bg-gray-100'
                      }`}
                      style={{ color: SEVERITY_OPTIONS.find(s => s.value === oncr.severity)?.textColor }}
                    >
                      {oncr.severity?.toUpperCase()}
                    </span>
                  </div>
                  
                  {/* Department on separate line for mobile */}
                  <div className="mb-2">
                    <span className={`inline-block px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs ${BMS_DEPARTMENTS[oncr.department_code]?.color || 'bg-gray-100'}`}>
                      {oncr.department_code} - <span className="hidden sm:inline">{oncr.department}</span>
                      <span className="sm:hidden">{oncr.department?.split(' ')[0]}</span>
                    </span>
                  </div>
                  
                  {/* NC Type Badge */}
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-[10px] sm:text-xs text-slate-500">Type:</span>
                    <span className="px-1.5 sm:px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] sm:text-xs capitalize">
                      {oncr.nc_type || 'Internal'} NC
                    </span>
                    {oncr.area_identified && (
                      <>
                        <span className="text-[10px] sm:text-xs text-slate-500">Area:</span>
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs capitalize">
                          {oncr.area_identified?.replace('_', ' ')}
                        </span>
                      </>
                    )}
                  </div>
                  
                  <p className="text-slate-600 text-sm line-clamp-2 mb-2">{oncr.description}</p>
                  
                  {/* Meta info */}
                  <div className="flex items-center gap-4 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {oncr.created_by_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {format(new Date(oncr.created_at), 'MMM dd, yyyy')}
                    </span>
                    <span className={`flex items-center gap-1 px-2 py-0.5 rounded ${getAgingColor(getDaysOpen(oncr.created_at))}`}>
                      <Timer className="h-3 w-3" />
                      {getDaysOpen(oncr.created_at)} days
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 ml-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    oncr.status === 'closed' ? 'bg-green-100 text-green-800' :
                    oncr.status === 'pending_approval' ? 'bg-purple-100 text-purple-800' :
                    oncr.status === 'verification' ? 'bg-blue-100 text-blue-800' :
                    oncr.status === 'capa_required' ? 'bg-orange-100 text-orange-800' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {oncr.status?.replace('_', ' ')}
                  </span>
                  {oncr.linked_capa_id && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                      CAPA: {oncr.linked_capa_job_number || oncr.linked_capa_number || oncr.linked_capa_id?.slice(0, 8)}
                    </span>
                  )}
                  <ChevronRight className="h-5 w-5 text-slate-400" />
                </div>
              </div>
            </div>
          ))}
          {filteredOncrs.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Factory className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>No ONCRs found</p>
            </div>
          )}
        </div>
      ) : (
        <ONCRDetailView 
          oncr={selectedOncr} 
          onBack={() => {
            setSelectedOncr(null);
            // Clear URL params when going back
            if (highlightId) {
              navigate('/oncrs', { replace: true });
            }
          }}
          onUpdate={(updated) => {
            setSelectedOncr(updated);
            fetchOncrs();
          }}
          user={user}
          fromCapa={!!highlightId}
        />
      )}

      {/* New ONCR Modal */}
      {showNewModal && (
        <NewONCRModal
          onClose={() => setShowNewModal(false)}
          onCreated={() => {
            fetchOncrs();
            setShowNewModal(false);
          }}
          user={user}
        />
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Upload className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Bulk Upload ONCRs</h2>
                  <p className="text-sm text-slate-500">Upload multiple ONCRs from an Excel file</p>
                </div>
              </div>
              <button
                onClick={closeBulkUploadModal}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 overflow-y-auto flex-1 space-y-4">
              {/* Step 1: Download Template */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                  <span className="w-6 h-6 bg-blue-600 text-white rounded-full text-xs flex items-center justify-center">1</span>
                  Download Template
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  Download the Excel template with predefined columns and dropdown validations.
                </p>
                <button
                  onClick={handleDownloadTemplate}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium text-slate-700"
                >
                  <Download className="w-4 h-4" />
                  Download Template
                </button>
              </div>

              {/* Step 2: Upload File */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                  <span className="w-6 h-6 bg-blue-600 text-white rounded-full text-xs flex items-center justify-center">2</span>
                  Upload Completed File
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  Fill in your ONCR data and upload the completed Excel file.
                </p>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".xlsx,.xls"
                  onChange={(e) => {
                    setBulkUploadFile(e.target.files[0]);
                    setBulkUploadResult(null);
                  }}
                  className="hidden"
                />
                
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium text-slate-700"
                  >
                    <FileText className="w-4 h-4" />
                    Select File
                  </button>
                  
                  {bulkUploadFile && (
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-slate-700">{bulkUploadFile.name}</span>
                      <button
                        onClick={() => setBulkUploadFile(null)}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Upload Results */}
              {bulkUploadResult && (
                <div className={`rounded-lg p-4 ${bulkUploadResult.summary.failed > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    {bulkUploadResult.summary.failed > 0 ? (
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    )}
                    <span className={bulkUploadResult.summary.failed > 0 ? 'text-amber-800' : 'text-green-800'}>
                      Upload Results
                    </span>
                  </h3>
                  
                  <div className="grid grid-cols-3 gap-4 mb-3">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-slate-800">{bulkUploadResult.summary.total_processed}</div>
                      <div className="text-xs text-slate-500">Total Processed</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{bulkUploadResult.summary.inserted}</div>
                      <div className="text-xs text-slate-500">Successfully Added</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">{bulkUploadResult.summary.failed}</div>
                      <div className="text-xs text-slate-500">Failed</div>
                    </div>
                  </div>

                  {/* Error Details */}
                  {bulkUploadResult.errors && bulkUploadResult.errors.length > 0 && (
                    <div className="mt-3 border-t border-amber-200 pt-3">
                      <h4 className="text-sm font-medium text-amber-800 mb-2">Errors:</h4>
                      <div className="max-h-40 overflow-y-auto space-y-2">
                        {bulkUploadResult.errors.map((error, idx) => (
                          <div key={idx} className="text-sm bg-white p-2 rounded border border-amber-100">
                            <span className="font-medium">Row {error.row}</span>
                            {error.job_number && <span className="text-slate-500"> ({error.job_number})</span>}
                            <ul className="list-disc list-inside text-red-600 mt-1">
                              {error.errors.map((err, errIdx) => (
                                <li key={errIdx}>{err}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-200 bg-slate-50">
              <button
                onClick={closeBulkUploadModal}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
              >
                {bulkUploadResult?.summary?.inserted > 0 ? 'Close' : 'Cancel'}
              </button>
              <button
                onClick={handleBulkUpload}
                disabled={!bulkUploadFile || bulkUploadLoading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
              >
                {bulkUploadLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload ONCRs
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ONCR Detail View with Collapsible Sections (Unifize-style)
const ONCRDetailView = ({ oncr, onBack, onUpdate, user, fromCapa = false }) => {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({
    basic_info: true,
    disposition_actions: false,
    verification: false,
    corrective_action: false,
    completion_approval: false
  });
  const [showSignatureModal, setShowSignatureModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [localOncr, setLocalOncr] = useState(oncr);
  const [linkedCapaDetails, setLinkedCapaDetails] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    setLocalOncr(oncr);
    setEditForm({
      nc_type: oncr.nc_type || 'internal',
      severity: oncr.severity || 'minor',
      area_identified: oncr.area_identified || '',
      job_number: oncr.job_number || '',
      description: oncr.description || '',
      department: oncr.department || '',
      department_code: oncr.department_code || '',
      process_area: oncr.process_area || '',
      product_affected: oncr.product_affected || '',
      batch_number: oncr.batch_number || '',
      quantity_affected: oncr.quantity_affected || 0,
      location: oncr.location || '',
      defect_type: oncr.defect_type || '',
      immediate_action: oncr.immediate_action || '',
      containment_action: oncr.containment_action || ''
    });
    // Fetch linked CAPA details if exists
    if (oncr.linked_capa_id) {
      fetchLinkedCapa(oncr.linked_capa_id);
    }
  }, [oncr]);

  const fetchLinkedCapa = async (capaId) => {
    try {
      const response = await apiClient.get(`/enhanced-capas/${capaId}`);
      setLinkedCapaDetails(response.data);
    } catch (error) {
      console.error('Error fetching linked CAPA:', error);
    }
  };

  const navigateToCapa = () => {
    if (localOncr.linked_capa_id) {
      navigate(`/capas?id=${localOncr.linked_capa_id}&source=oncr&source_ref=${localOncr.oncr_number}`);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getDaysOpen = (createdAt) => {
    return differenceInDays(new Date(), new Date(createdAt));
  };

  const updateOncr = async (updates) => {
    setSaving(true);
    try {
      await apiClient.patch(`/oncrs/${localOncr.id}`, updates);
      const response = await apiClient.get(`/oncrs/${localOncr.id}`);
      setLocalOncr(response.data);
      onUpdate(response.data);
      toast.success('ONCR updated successfully');
    } catch (error) {
      console.error('Error updating ONCR:', error);
      toast.error('Failed to update ONCR');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    await updateOncr(editForm);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditForm({
      nc_type: localOncr.nc_type || 'internal',
      severity: localOncr.severity || 'minor',
      area_identified: localOncr.area_identified || '',
      job_number: localOncr.job_number || '',
      description: localOncr.description || '',
      department: localOncr.department || '',
      department_code: localOncr.department_code || '',
      process_area: localOncr.process_area || '',
      product_affected: localOncr.product_affected || '',
      batch_number: localOncr.batch_number || '',
      quantity_affected: localOncr.quantity_affected || 0,
      location: localOncr.location || '',
      defect_type: localOncr.defect_type || '',
      immediate_action: localOncr.immediate_action || '',
      containment_action: localOncr.containment_action || ''
    });
    setIsEditing(false);
  };

  const handleDispositionAction = async (action) => {
    const dispositionActions = localOncr.disposition_actions || [];
    dispositionActions.push({
      id: `DA-${Date.now()}`,
      action: action.action,
      description: action.description,
      assigned_to: action.assigned_to,
      assigned_to_name: action.assigned_to_name,
      target_date: action.target_date,
      status: 'pending',
      created_at: new Date().toISOString()
    });
    
    await updateOncr({ 
      disposition_actions: dispositionActions,
      status: 'disposition'
    });
  };

  const handleVerificationApproval = async (signatureData, signatureType) => {
    await updateOncr({
      verification_signature: signatureData,
      verification_signature_type: signatureType,
      verification_by: user?.id,
      verification_by_name: user?.name,
      verification_date: new Date().toISOString(),
      status: 'pending_approval'
    });
    setShowSignatureModal(null);
  };

  const handleFinalApproval = async (signatureData, signatureType) => {
    await updateOncr({
      final_approval_signature: signatureData,
      final_approval_signature_type: signatureType,
      final_approved_by: user?.id,
      final_approved_by_name: user?.name,
      final_approval_date: new Date().toISOString(),
      closed_date: new Date().toISOString(),
      closed_by: user?.id,
      status: 'closed'
    });
    setShowSignatureModal(null);
  };

  const createCapa = async () => {
    try {
      const capaData = {
        capa_number: '',
        title: `CAPA for ${localOncr.oncr_number}`,
        capa_type: 'corrective',
        source_type: 'oncr',
        source_id: localOncr.id,
        source_reference: localOncr.oncr_number,
        department: localOncr.department,
        department_code: localOncr.department_code,
        problem_statement: localOncr.description,
        severity: localOncr.severity,
        owner_id: user?.id || 'current-user',
        owner_name: user?.name || 'Current User',
        due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        created_by: user?.id || 'current-user',
        created_by_name: user?.name || 'Current User',
        job_number: localOncr.job_number
      };
      
      const response = await apiClient.post('/enhanced-capas', capaData);
      await updateOncr({ 
        linked_capa_id: response.data.id,
        status: 'capa_required'
      });
      alert('CAPA created successfully!');
    } catch (error) {
      console.error('Error creating CAPA:', error);
    }
  };

  const SectionHeader = ({ title, icon: Icon, section, badge, badgeColor = 'bg-red-500' }) => (
    <button
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-4 bg-purple-50 hover:bg-purple-100 transition-colors rounded-t-xl"
    >
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-purple-700" />
        <span className="font-semibold text-purple-800">{title}</span>
        {badge && (
          <span className={`px-2 py-0.5 ${badgeColor} text-white text-xs rounded-full`}>
            {badge}
          </span>
        )}
      </div>
      {expandedSections[section] ? (
        <ChevronDown className="h-5 w-5 text-purple-600" />
      ) : (
        <ChevronRight className="h-5 w-5 text-purple-600" />
      )}
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
        data-testid="back-to-list"
      >
        <ChevronRight className="h-4 w-4 rotate-180" />
        Back to List
      </button>

      {/* CAPA Link Banner (when coming from CAPA) */}
      {fromCapa && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-purple-600" />
              <p className="font-medium text-purple-700">Viewing ONCR linked to CAPA</p>
            </div>
            <button
              onClick={() => {
                if (localOncr.linked_capa_id) {
                  navigate(`/capas?id=${localOncr.linked_capa_id}`);
                } else {
                  navigate('/capas');
                }
              }}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
              data-testid="back-to-capa-btn"
            >
              <ChevronRight className="h-4 w-4 rotate-180" />
              Back to CAPA
            </button>
          </div>
        </div>
      )}

      {/* Main Header Card */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {/* Header Bar - Unifize Style */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold">{localOncr.oncr_number}</h2>
              <p className="text-purple-100 text-sm mt-1">
                {localOncr.nc_type?.charAt(0).toUpperCase() + localOncr.nc_type?.slice(1) || 'Internal'} NC - {localOncr.description?.slice(0, 60)}...
              </p>
            </div>
            <div className="text-right">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                localOncr.status === 'closed' ? 'bg-green-500/20 text-green-100' :
                localOncr.status === 'pending_approval' ? 'bg-yellow-500/20 text-yellow-100' :
                'bg-white/20 text-white'
              }`}>
                {localOncr.status?.replace('_', ' ').toUpperCase()}
              </span>
            </div>
          </div>
          
          {/* Meta Row */}
          <div className="flex items-center gap-6 mt-4 text-sm text-purple-100">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span>{localOncr.created_by_name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>{format(new Date(localOncr.created_at), 'MMM dd, yyyy')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Timer className="h-4 w-4" />
              <span>{getDaysOpen(localOncr.created_at)} days open</span>
            </div>
            <div className="flex items-center gap-2">
              <span 
                className={`px-2 py-0.5 rounded text-xs font-medium ${
                  SEVERITY_OPTIONS.find(s => s.value === localOncr.severity)?.color || 'bg-gray-100'
                }`}
                style={{ color: SEVERITY_OPTIONS.find(s => s.value === localOncr.severity)?.textColor }}
              >
                {localOncr.severity?.toUpperCase()}
              </span>
            </div>
            {localOncr.job_number && (
              <div className="flex items-center gap-2 bg-white/10 px-2 py-1 rounded">
                <Hash className="h-4 w-4" />
                <span className="font-medium">Job: {localOncr.job_number}</span>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3 p-4 bg-slate-50 border-b border-slate-200">
          {!localOncr.linked_capa_id && localOncr.status !== 'closed' && (
            <button
              onClick={createCapa}
              className="flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 text-sm"
            >
              <Plus className="h-4 w-4" />
              Create CAPA
            </button>
          )}
          {localOncr.linked_capa_id && (
            <span className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium">
              CAPA: {linkedCapaDetails?.job_number || linkedCapaDetails?.capa_number || localOncr.linked_capa_id?.slice(0, 8)}
            </span>
          )}
          <button
            onClick={async () => {
              try {
                const response = await apiClient.get(`/oncrs/${localOncr.id}/report/pdf`, {
                  responseType: 'blob'
                });
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `ONCR_${localOncr.oncr_number}_${new Date().toISOString().split('T')[0]}.pdf`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                toast.success('PDF report downloaded successfully');
              } catch (error) {
                console.error('Error downloading PDF:', error);
                toast.error('Failed to download PDF report');
              }
            }}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm ml-auto"
            data-testid="download-oncr-pdf"
          >
            <Download className="h-4 w-4" />
            Download PDF Report
          </button>
        </div>

        {/* Collapsible Sections */}
        <div className="divide-y divide-slate-200">
          
          {/* BASIC INFO Section */}
          <div>
            <div className="flex items-center justify-between">
              <SectionHeader 
                title="BASIC INFO" 
                icon={FileText} 
                section="basic_info" 
              />
              {expandedSections.basic_info && localOncr.status !== 'closed' && (
                <div className="pr-4">
                  {isEditing ? (
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveEdit}
                        disabled={saving}
                        className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
                      >
                        <Save className="h-3 w-3" />
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="flex items-center gap-1 px-3 py-1 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50"
                      >
                        <X className="h-3 w-3" />
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex items-center gap-1 px-3 py-1 border border-blue-600 text-blue-600 rounded text-sm hover:bg-blue-50"
                      data-testid="edit-oncr-btn"
                    >
                      <Edit2 className="h-3 w-3" />
                      Edit
                    </button>
                  )}
                </div>
              )}
            </div>
            {expandedSections.basic_info && (
              <div className="p-6 space-y-4">
                {isEditing ? (
                  /* Edit Mode */
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Type</label>
                        <select
                          value={editForm.nc_type}
                          onChange={(e) => setEditForm({...editForm, nc_type: e.target.value})}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="internal">Internal</option>
                          <option value="external">External</option>
                          <option value="supplier">Supplier</option>
                          <option value="customer">Customer</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Severity</label>
                        <select
                          value={editForm.severity}
                          onChange={(e) => setEditForm({...editForm, severity: e.target.value})}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          {SEVERITY_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Area Identified</label>
                        <select
                          value={editForm.area_identified}
                          onChange={(e) => setEditForm({...editForm, area_identified: e.target.value})}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select Area</option>
                          <option value="receiving_inspection">Receiving Inspection</option>
                          <option value="in_process">In Process</option>
                          <option value="final_inspection">Final Inspection</option>
                          <option value="customer_return">Customer Return</option>
                          <option value="audit">Audit</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Job Number</label>
                        <input
                          type="text"
                          value={editForm.job_number}
                          onChange={(e) => setEditForm({...editForm, job_number: e.target.value})}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">Description of the problem</label>
                      <textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                        rows={3}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Department</label>
                        <select
                          value={editForm.department_code}
                          onChange={(e) => {
                            const deptCode = e.target.value;
                            const deptName = BMS_DEPARTMENTS[deptCode]?.name || '';
                            setEditForm({...editForm, department_code: deptCode, department: deptName});
                          }}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          {Object.entries(BMS_DEPARTMENTS).map(([code, dept]) => (
                            <option key={code} value={code}>{code} - {dept.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Process Area</label>
                        <input
                          type="text"
                          value={editForm.process_area}
                          onChange={(e) => setEditForm({...editForm, process_area: e.target.value})}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Product Affected</label>
                        <input
                          type="text"
                          value={editForm.product_affected}
                          onChange={(e) => setEditForm({...editForm, product_affected: e.target.value})}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Batch Number</label>
                        <input
                          type="text"
                          value={editForm.batch_number}
                          onChange={(e) => setEditForm({...editForm, batch_number: e.target.value})}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Quantity Affected</label>
                        <input
                          type="number"
                          value={editForm.quantity_affected}
                          onChange={(e) => setEditForm({...editForm, quantity_affected: parseInt(e.target.value) || 0})}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Location</label>
                        <input
                          type="text"
                          value={editForm.location}
                          onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  /* View Mode */
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="text-xs text-slate-500">Type</label>
                        <p className="font-medium capitalize">{localOncr.nc_type || 'Internal'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Severity</label>
                        <p 
                          className={`inline-block px-2 py-0.5 rounded text-sm font-medium ${
                            SEVERITY_OPTIONS.find(s => s.value === localOncr.severity)?.color || ''
                          }`}
                          style={{ color: SEVERITY_OPTIONS.find(s => s.value === localOncr.severity)?.textColor }}
                        >
                          {localOncr.severity?.charAt(0).toUpperCase() + localOncr.severity?.slice(1)}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Area Identified</label>
                        <p className="font-medium capitalize">{localOncr.area_identified?.replace('_', ' ') || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Job Number</label>
                        <p className="font-medium text-indigo-600">{localOncr.job_number || 'N/A'}</p>
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-xs text-slate-500">Description of the problem</label>
                      <p className="mt-1 text-slate-700 bg-slate-50 p-3 rounded-lg">{localOncr.description}</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-slate-500">Department</label>
                        <p className="font-medium">{localOncr.department_code} - {localOncr.department}</p>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Process Area</label>
                        <p className="font-medium">{localOncr.process_area || 'N/A'}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="text-xs text-slate-500">Product Affected</label>
                        <p className="font-medium">{localOncr.product_affected || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Batch Number</label>
                        <p className="font-medium">{localOncr.batch_number || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Quantity Affected</label>
                        <p className="font-medium">{localOncr.quantity_affected || 0}</p>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Location</label>
                        <p className="font-medium">{localOncr.location || 'N/A'}</p>
                      </div>
                    </div>
                  </>
                )}

                {localOncr.failure_codes?.length > 0 && (
                  <div>
                    <label className="text-xs text-slate-500">Failure Mode/Reason Codes</label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {localOncr.failure_codes.map((code, idx) => (
                        <span key={idx} className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-sm">
                          #{code}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {localOncr.images?.length > 0 && (
                  <div>
                    <label className="text-xs text-slate-500">Uploaded Images</label>
                    <div className="flex gap-2 mt-2">
                      {localOncr.images.map((img, idx) => (
                        <div key={idx} className="w-20 h-20 bg-slate-100 rounded-lg flex items-center justify-center">
                          <Image className="h-8 w-8 text-slate-400" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Associated Risks */}
                {localOncr.associated_risks?.length > 0 && (
                  <div>
                    <label className="text-xs text-slate-500">Associated Risks</label>
                    <div className="space-y-2 mt-1">
                      {localOncr.associated_risks.map((risk, idx) => (
                        <div key={idx} className="p-2 bg-red-50 border border-red-200 rounded-lg text-sm">
                          {risk}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* DISPOSITION ACTION(S) Section */}
          <div>
            <SectionHeader 
              title="DISPOSITION ACTION(S)" 
              icon={ClipboardList} 
              section="disposition_actions"
              badge={localOncr.disposition_actions?.filter(a => a.status === 'pending').length || null}
            />
            {expandedSections.disposition_actions && (
              <div className="p-6 space-y-4">
                <div>
                  <label className="text-xs text-slate-500">Disposition definition</label>
                  <p className="mt-1 text-slate-700 bg-amber-50 p-3 rounded-lg border border-amber-200">
                    {localOncr.disposition_definition || localOncr.immediate_action || 'No disposition defined yet.'}
                  </p>
                </div>

                <div>
                  <label className="text-xs text-slate-500 mb-2 block">Disposition action(s)</label>
                  <div className="space-y-3">
                    {(localOncr.disposition_actions || []).map((action, idx) => (
                      <div key={action.id || idx} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs text-slate-500">#{action.id?.split('-')[1] || idx + 1}</span>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                action.status === 'completed' ? 'bg-green-100 text-green-700' :
                                action.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {action.status?.toUpperCase() || 'PENDING'}
                              </span>
                            </div>
                            <p className="font-medium text-slate-800">{action.description || action.action}</p>
                          </div>
                          <div className="text-right text-sm">
                            <div className="flex items-center gap-1 text-slate-500">
                              <User className="h-3 w-3" />
                              {action.assigned_to_name}
                            </div>
                            <div className="flex items-center gap-1 text-slate-500 mt-1">
                              <Calendar className="h-3 w-3" />
                              {action.target_date ? format(new Date(action.target_date), 'MMM dd, yyyy') : 'No date'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {localOncr.status !== 'closed' && (
                    <AddDispositionActionForm onAdd={handleDispositionAction} />
                  )}
                </div>

                <div>
                  <label className="text-xs text-slate-500">Current Disposition</label>
                  <select
                    value={localOncr.disposition || 'pending'}
                    onChange={(e) => updateOncr({ disposition: e.target.value })}
                    disabled={localOncr.status === 'closed'}
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg"
                  >
                    {DISPOSITION_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* DISPOSITION VERIFICATION AND CLOSURE Section */}
          <div>
            <SectionHeader 
              title="DISPOSITION VERIFICATION AND CLOSURE" 
              icon={FileCheck} 
              section="verification"
              badge={!localOncr.verification_signature ? '!' : null}
              badgeColor="bg-amber-500"
            />
            {expandedSections.verification && (
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-slate-500">Verified by</label>
                    {localOncr.verification_by_name ? (
                      <p className="mt-1 font-medium">{localOncr.verification_by_name}</p>
                    ) : (
                      <p className="mt-1 text-slate-400 italic">Not verified yet</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Verified on</label>
                    {localOncr.verification_date ? (
                      <p className="mt-1 font-medium">{format(new Date(localOncr.verification_date), 'MMM dd, yyyy')}</p>
                    ) : (
                      <p className="mt-1 text-slate-400 italic">Not verified yet</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-xs text-slate-500 mb-2 block">Verification approval</label>
                  {localOncr.verification_signature ? (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2 text-green-700 mb-2">
                        <CheckCircle className="h-5 w-5" />
                        <span className="font-medium">Verified</span>
                      </div>
                      {localOncr.verification_signature_type === 'drawn' ? (
                        <img 
                          src={localOncr.verification_signature} 
                          alt="Verification Signature" 
                          className="max-h-16 border border-slate-200 rounded bg-white p-2"
                        />
                      ) : (
                        <p className="font-signature text-xl italic">{localOncr.verification_signature}</p>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 border border-amber-300 bg-amber-50 rounded-lg">
                      <div className="flex items-center gap-2 text-amber-700 mb-3">
                        <AlertCircle className="h-5 w-5" />
                        <span>Verification signature required</span>
                      </div>
                      {localOncr.status !== 'closed' && (localOncr.disposition_actions?.length > 0 || localOncr.disposition !== 'pending') && (
                        <button
                          onClick={() => setShowSignatureModal('verification')}
                          className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                        >
                          <PenTool className="h-4 w-4" />
                          Add Verification Signature
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* CORRECTIVE ACTION REQUEST Section */}
          <div>
            <SectionHeader 
              title="CORRECTIVE ACTION REQUEST" 
              icon={AlertTriangle} 
              section="corrective_action"
              badge={localOncr.linked_capa_id ? 'Linked' : null}
              badgeColor="bg-purple-500"
            />
            {expandedSections.corrective_action && (
              <div className="p-6 space-y-4">
                {localOncr.linked_capa_id ? (
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-700 font-medium">CAPA has been created for this ONCR</p>
                        {linkedCapaDetails && (
                          <p className="text-sm text-purple-600 mt-1">
                            <span className="font-semibold">{linkedCapaDetails.capa_number}</span>: {linkedCapaDetails.title}
                          </p>
                        )}
                        <p className="text-xs text-purple-500 mt-1">ID: {localOncr.linked_capa_id?.slice(0, 12)}...</p>
                      </div>
                      <button
                        onClick={navigateToCapa}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                        data-testid="go-to-capa-btn"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Go to CAPA
                      </button>
                    </div>
                    {linkedCapaDetails && (
                      <div className="mt-3 pt-3 border-t border-purple-200 grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-purple-500">Status:</span>
                          <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                            linkedCapaDetails.status === 'closed' ? 'bg-green-100 text-green-700' :
                            linkedCapaDetails.status === 'investigation' ? 'bg-blue-100 text-blue-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {linkedCapaDetails.status?.toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <span className="text-purple-500">Due:</span>
                          <span className="ml-2 text-purple-700">
                            {linkedCapaDetails.due_date ? format(new Date(linkedCapaDetails.due_date), 'MMM dd, yyyy') : 'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="text-purple-500">Owner:</span>
                          <span className="ml-2 text-purple-700">{linkedCapaDetails.owner_name}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500">No CAPA linked to this ONCR</p>
                    {localOncr.status !== 'closed' && (
                      <button
                        onClick={createCapa}
                        className="mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                      >
                        Create CAPA Request
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* NON-CONFORMANCE COMPLETION APPROVAL(S) Section */}
          <div>
            <SectionHeader 
              title="NON-CONFORMANCE COMPLETION APPROVAL(S)" 
              icon={ShieldCheck} 
              section="completion_approval"
              badge={localOncr.status === 'closed' ? '✓' : '!'}
              badgeColor={localOncr.status === 'closed' ? 'bg-green-500' : 'bg-red-500'}
            />
            {expandedSections.completion_approval && (
              <div className="p-6 space-y-4">
                {/* Final Approval */}
                <div>
                  <label className="text-xs text-slate-500 mb-2 block">Final Approval</label>
                  {localOncr.final_approval_signature ? (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2 text-green-700 mb-2">
                        <CheckCircle className="h-5 w-5" />
                        <span className="font-medium">Approved & Closed</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div>
                          <label className="text-xs text-slate-500">Approved by</label>
                          <p className="font-medium">{localOncr.final_approved_by_name}</p>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Approved on</label>
                          <p className="font-medium">{format(new Date(localOncr.final_approval_date), 'MMM dd, yyyy HH:mm')}</p>
                        </div>
                      </div>
                      {localOncr.final_approval_signature_type === 'drawn' ? (
                        <img 
                          src={localOncr.final_approval_signature} 
                          alt="Final Approval Signature" 
                          className="max-h-16 border border-slate-200 rounded bg-white p-2"
                        />
                      ) : (
                        <p className="font-signature text-xl italic">{localOncr.final_approval_signature}</p>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 border border-red-300 bg-red-50 rounded-lg">
                      <div className="flex items-center gap-2 text-red-700 mb-3">
                        <AlertCircle className="h-5 w-5" />
                        <span>Final approval signature required to close ONCR</span>
                      </div>
                      {localOncr.verification_signature && localOncr.status !== 'closed' && (
                        <button
                          onClick={() => setShowSignatureModal('final')}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                          <PenTool className="h-4 w-4" />
                          Add Final Approval Signature
                        </button>
                      )}
                      {!localOncr.verification_signature && (
                        <p className="text-sm text-red-600">Complete verification step first</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Closure Summary */}
                {localOncr.status === 'closed' && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold text-slate-700 mb-2">Closure Summary</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <label className="text-xs text-slate-500">Total Days Open</label>
                        <p className="font-medium">{getDaysOpen(localOncr.created_at)} days</p>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Closed Date</label>
                        <p className="font-medium">{format(new Date(localOncr.closed_date), 'MMM dd, yyyy')}</p>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Disposition</label>
                        <p className="font-medium capitalize">{localOncr.disposition?.replace('_', ' ')}</p>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">CAPA Required</label>
                        <p className="font-medium">{localOncr.linked_capa_id ? 'Yes' : 'No'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Signature Modal */}
      {showSignatureModal && (
        <SignatureModal
          title={showSignatureModal === 'verification' ? 'Verification Signature' : 'Final Approval Signature'}
          onClose={() => setShowSignatureModal(null)}
          onSave={showSignatureModal === 'verification' ? handleVerificationApproval : handleFinalApproval}
        />
      )}
    </div>
  );
};

// Add Disposition Action Form
const AddDispositionActionForm = ({ onAdd }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    action: '',
    description: '',
    assigned_to: '',
    assigned_to_name: '',
    target_date: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd(formData);
    setFormData({ action: '', description: '', assigned_to: '', assigned_to_name: '', target_date: '' });
    setShowForm(false);
  };

  if (!showForm) {
    return (
      <button
        onClick={() => setShowForm(true)}
        className="mt-3 flex items-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg text-sm"
      >
        <Plus className="h-4 w-4" />
        Add Disposition Action
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-3 p-4 border border-slate-200 rounded-lg bg-slate-50">
      <div className="grid grid-cols-2 gap-3 mb-3">
        <input
          type="text"
          placeholder="Action type (e.g., Rework, Recalibrate)"
          value={formData.action}
          onChange={(e) => setFormData({ ...formData, action: e.target.value })}
          required
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
        />
        <input
          type="text"
          placeholder="Assigned to"
          value={formData.assigned_to_name}
          onChange={(e) => setFormData({ ...formData, assigned_to_name: e.target.value, assigned_to: e.target.value })}
          required
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
        />
      </div>
      <textarea
        placeholder="Description of the action..."
        value={formData.description}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        required
        rows={2}
        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mb-3"
      />
      <div className="flex items-center gap-3">
        <input
          type="date"
          value={formData.target_date}
          onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
          required
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
        />
        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
          Add Action
        </button>
        <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm">
          Cancel
        </button>
      </div>
    </form>
  );
};

// Signature Modal Component
const SignatureModal = ({ title, onClose, onSave }) => {
  const [signatureType, setSignatureType] = useState('typed');
  const [typedSignature, setTypedSignature] = useState('');
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    if (signatureType === 'drawn' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#1e40af';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  }, [signatureType]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const handleSave = () => {
    if (signatureType === 'typed') {
      if (!typedSignature.trim()) {
        alert('Please enter your signature');
        return;
      }
      onSave(typedSignature, 'typed');
    } else {
      const canvas = canvasRef.current;
      const dataUrl = canvas.toDataURL('image/png');
      onSave(dataUrl, 'drawn');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-800">{title}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setSignatureType('typed')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
              signatureType === 'typed' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
            }`}
          >
            Type Signature
          </button>
          <button
            onClick={() => setSignatureType('drawn')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
              signatureType === 'drawn' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
            }`}
          >
            Draw Signature
          </button>
        </div>

        {signatureType === 'typed' ? (
          <div>
            <input
              type="text"
              placeholder="Type your full name"
              value={typedSignature}
              onChange={(e) => setTypedSignature(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg text-lg"
            />
            {typedSignature && (
              <div className="mt-3 p-4 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Preview:</p>
                <p className="font-signature text-2xl italic text-blue-800">{typedSignature}</p>
              </div>
            )}
          </div>
        ) : (
          <div>
            <canvas
              ref={canvasRef}
              width={400}
              height={150}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              className="w-full border border-slate-300 rounded-lg cursor-crosshair"
            />
            <button
              onClick={clearCanvas}
              className="mt-2 text-sm text-blue-600 hover:text-blue-700"
            >
              Clear
            </button>
          </div>
        )}

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Save Signature
          </button>
        </div>
      </div>
    </div>
  );
};

// New ONCR Modal Component
const NewONCRModal = ({ onClose, onCreated, user }) => {
  const [formData, setFormData] = useState({
    oncr_number: '',
    job_number: '',
    nc_type: 'internal',
    department: '',
    department_code: '',
    area_identified: 'in_process',
    process_area: '',
    product_affected: '',
    batch_number: '',
    quantity_affected: 0,
    defect_type: 'process',
    severity: 'major',
    description: '',
    immediate_action: '',
    containment_action: '',
    evidence: '',
    disposition: 'pending',
    location: '',
    failure_codes: [],
    associated_risks: []
  });
  const [saving, setSaving] = useState(false);
  const [newFailureCode, setNewFailureCode] = useState('');
  const [newRisk, setNewRisk] = useState('');

  const handleDeptChange = (code) => {
    setFormData({
      ...formData,
      department_code: code,
      department: BMS_DEPARTMENTS[code]?.name || ''
    });
  };

  const addFailureCode = () => {
    if (newFailureCode.trim()) {
      setFormData({
        ...formData,
        failure_codes: [...formData.failure_codes, newFailureCode.trim()]
      });
      setNewFailureCode('');
    }
  };

  const addRisk = () => {
    if (newRisk.trim()) {
      setFormData({
        ...formData,
        associated_risks: [...formData.associated_risks, newRisk.trim()]
      });
      setNewRisk('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await apiClient.post('/oncrs', {
        ...formData,
        created_by: user?.id || 'current-user',
        created_by_name: user?.name || 'Current User'
      });
      toast.success(`ONCR ${response.data.oncr_number} created successfully`);
      onCreated();
      onClose(); // Ensure modal closes
    } catch (error) {
      console.error('Error creating ONCR:', error);
      if (error.response?.data?.detail) {
        const details = error.response.data.detail;
        if (Array.isArray(details)) {
          const missingFields = details.map(d => d.loc?.[1] || d.msg).join(', ');
          toast.error(`Missing required fields: ${missingFields}`);
        } else {
          toast.error(details);
        }
      } else {
        toast.error('Failed to create ONCR. Please check all required fields.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-800">New Operational Non-Conformance</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Job Number - Prominent */}
          <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
            <label className="block text-sm font-medium text-indigo-700 mb-1">
              Job Number <span className="text-indigo-400">(Required for traceability)</span>
            </label>
            <input
              type="text"
              required
              value={formData.job_number}
              onChange={(e) => setFormData({ ...formData, job_number: e.target.value })}
              className="w-full px-4 py-2 border border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g., JOB-2026-0145"
              data-testid="input-job-number"
            />
          </div>

          {/* Type and Severity Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">NC Type</label>
              <select
                value={formData.nc_type}
                onChange={(e) => setFormData({ ...formData, nc_type: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              >
                {NC_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Severity</label>
              <select
                value={formData.severity}
                onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              >
                {SEVERITY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Area Identified</label>
              <select
                value={formData.area_identified}
                onChange={(e) => setFormData({ ...formData, area_identified: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              >
                {AREA_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Defect Type</label>
              <select
                value={formData.defect_type}
                onChange={(e) => setFormData({ ...formData, defect_type: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              >
                <option value="process">Process</option>
                <option value="documentation">Documentation</option>
                <option value="equipment">Equipment</option>
                <option value="human_error">Human Error</option>
                <option value="material">Material</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          {/* Department and Process Area */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Department</label>
              <select
                required
                value={formData.department_code}
                onChange={(e) => handleDeptChange(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                data-testid="input-department"
              >
                <option value="">Select Department</option>
                {Object.entries(BMS_DEPARTMENTS).map(([code, info]) => (
                  <option key={code} value={code}>{code} - {info.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Process Area / Location</label>
              <input
                type="text"
                required
                value={formData.process_area}
                onChange={(e) => setFormData({ ...formData, process_area: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                placeholder="e.g., Assembly Line 2, Station 4"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description of the problem</label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              placeholder="Describe the non-conformance in detail..."
              data-testid="input-description"
            />
          </div>

          {/* Product Details */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Product Affected</label>
              <input
                type="text"
                value={formData.product_affected}
                onChange={(e) => setFormData({ ...formData, product_affected: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                placeholder="Product name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Batch Number</label>
              <input
                type="text"
                value={formData.batch_number}
                onChange={(e) => setFormData({ ...formData, batch_number: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                placeholder="Batch #"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Qty Affected</label>
              <input
                type="number"
                value={formData.quantity_affected}
                onChange={(e) => setFormData({ ...formData, quantity_affected: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                placeholder="e.g., Washington"
              />
            </div>
          </div>

          {/* Failure Codes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Failure Mode/Reason Codes</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newFailureCode}
                onChange={(e) => setNewFailureCode(e.target.value)}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg"
                placeholder="e.g., FM-001"
              />
              <button
                type="button"
                onClick={addFailureCode}
                className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            {formData.failure_codes.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.failure_codes.map((code, idx) => (
                  <span key={idx} className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-sm flex items-center gap-1">
                    #{code}
                    <button
                      type="button"
                      onClick={() => setFormData({
                        ...formData,
                        failure_codes: formData.failure_codes.filter((_, i) => i !== idx)
                      })}
                      className="hover:text-orange-900"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Associated Risks */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Associated Risks</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newRisk}
                onChange={(e) => setNewRisk(e.target.value)}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg"
                placeholder="Describe potential risk..."
              />
              <button
                type="button"
                onClick={addRisk}
                className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            {formData.associated_risks.length > 0 && (
              <div className="space-y-2">
                {formData.associated_risks.map((risk, idx) => (
                  <div key={idx} className="p-2 bg-red-50 border border-red-200 rounded-lg text-sm flex items-start justify-between">
                    <span>{risk}</span>
                    <button
                      type="button"
                      onClick={() => setFormData({
                        ...formData,
                        associated_risks: formData.associated_risks.filter((_, i) => i !== idx)
                      })}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Immediate Action */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Immediate Action Taken</label>
            <textarea
              value={formData.immediate_action}
              onChange={(e) => setFormData({ ...formData, immediate_action: e.target.value })}
              rows={2}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              placeholder="What immediate action was taken to contain the issue?"
            />
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              data-testid="submit-oncr"
            >
              {saving ? 'Creating...' : 'Create ONCR'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ONCRManagement;
